import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import { MapPin, Navigation, RefreshCw, Phone, Clock, User } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const technicianIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#3b82f6" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#3b82f6"/>
      <circle cx="12" cy="12" r="6" fill="white"/>
      <circle cx="12" cy="12" r="3" fill="#3b82f6"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const faultLocationIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ef4444" width="32" height="40">
      <path d="M12 0C7.31 0 3.5 3.81 3.5 8.5C3.5 14.88 12 24 12 24S20.5 14.88 20.5 8.5C20.5 3.81 16.69 0 12 0ZM12 13C9.79 13 8 11.21 8 9S9.79 5 12 5 16 6.79 16 9 14.21 13 12 13Z" fill="#ef4444"/>
    </svg>
  `),
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -40],
});

// Map center updater component
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 14);
    }
  }, [center, map]);
  return null;
}

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TechnicianLocationMap = ({ faultId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchLocation = async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/faults/${faultId}/assigned-technician-location`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setData(result);
        setError(null);
      } else {
        setError('Konum bilgisi alınamadı');
      }
    } catch (err) {
      setError('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocation();
  }, [faultId]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(fetchLocation, 30000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, faultId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-slate-100 dark:bg-slate-800 rounded-xl">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-slate-100 dark:bg-slate-800 rounded-xl">
        <MapPin className="w-12 h-12 text-slate-400 mb-2" />
        <p className="text-slate-500">{error}</p>
        <button 
          onClick={fetchLocation}
          className="mt-2 px-4 py-2 bg-primary text-white rounded-lg text-sm"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  if (!data?.location) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-slate-100 dark:bg-slate-800 rounded-xl">
        <MapPin className="w-12 h-12 text-slate-400 mb-2" />
        <p className="text-slate-500">Usta konum bilgisi henüz mevcut değil</p>
        {data?.technician && (
          <p className="text-sm text-slate-400 mt-1">
            {data.technician.full_name} - {data.technician.status === 'available' ? 'Müsait' : data.technician.status === 'busy' ? 'Meşgul' : 'İzinli'}
          </p>
        )}
      </div>
    );
  }

  const techLocation = data.location;
  const faultLocation = data.fault_location;
  const techCoords = techLocation?.coordinates ? [techLocation.coordinates[1], techLocation.coordinates[0]] : null;
  const faultCoords = faultLocation ? [faultLocation.latitude, faultLocation.longitude] : null;
  const mapCenter = techCoords || faultCoords || [41.0082, 28.9784]; // Default: İstanbul

  // Calculate distance if both locations available
  let distance = null;
  if (techCoords && faultCoords) {
    const R = 6371; // Earth's radius in km
    const dLat = (faultCoords[0] - techCoords[0]) * Math.PI / 180;
    const dLon = (faultCoords[1] - techCoords[1]) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(techCoords[0] * Math.PI / 180) * Math.cos(faultCoords[0] * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    distance = (R * c).toFixed(1);
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Usta Konum Takibi</h3>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Otomatik güncelle
          </label>
          <button
            onClick={fetchLocation}
            className="p-2 rounded-lg bg-muted hover:bg-accent transition-colors"
            title="Yenile"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Technician Info */}
      {data.technician && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
            <User className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-medium">{data.technician.full_name}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <Phone className="w-3 h-3" />
              {data.technician.phone || 'Telefon yok'}
            </p>
          </div>
          <div className="text-right">
            <span className={`px-2 py-1 text-xs rounded-full ${
              data.technician.status === 'available' ? 'bg-green-100 text-green-700' :
              data.technician.status === 'busy' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {data.technician.status === 'available' ? 'Müsait' : 
               data.technician.status === 'busy' ? 'Meşgul' : 'İzinli'}
            </span>
            {distance && (
              <p className="text-xs text-muted-foreground mt-1">{distance} km uzakta</p>
            )}
          </div>
        </div>
      )}

      {/* ETA Bilgisi */}
      {data.eta && (
        <div className={`p-3 rounded-lg border ${
          data.eta.traffic === 'heavy' ? 'bg-red-50 dark:bg-red-900/20 border-red-200' :
          data.eta.traffic === 'moderate' ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200' :
          'bg-green-50 dark:bg-green-900/20 border-green-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-primary" />
              <div>
                <p className="font-semibold text-lg">
                  ~{data.eta.duration_minutes} dakika
                </p>
                <p className="text-xs text-muted-foreground">
                  {data.eta.distance_text} mesafe
                </p>
              </div>
            </div>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
              data.eta.traffic === 'heavy' ? 'bg-red-100 text-red-700' :
              data.eta.traffic === 'moderate' ? 'bg-orange-100 text-orange-700' :
              'bg-green-100 text-green-700'
            }`}>
              {data.eta.traffic === 'heavy' ? '🔴 Yoğun Trafik' :
               data.eta.traffic === 'moderate' ? '🟠 Orta Trafik' :
               '🟢 Hafif Trafik'}
            </div>
          </div>
        </div>
      )}

      {/* Last Update */}
      {data.location_updated_at && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Son güncelleme: {new Date(data.location_updated_at).toLocaleString('tr-TR')}
        </p>
      )}

      {/* Map */}
      <div className="h-80 rounded-xl overflow-hidden border border-border">
        <MapContainer
          center={mapCenter}
          zoom={14}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapUpdater center={mapCenter} />

          {/* Technician Marker */}
          {techCoords && (
            <>
              <Marker position={techCoords} icon={technicianIcon}>
                <Popup>
                  <div className="text-center">
                    <strong>{data.technician?.full_name || 'Usta'}</strong>
                    <br />
                    <span className="text-xs text-gray-500">
                      Doğruluk: {techLocation.accuracy?.toFixed(0) || '?'} m
                    </span>
                  </div>
                </Popup>
              </Marker>
              {/* Accuracy circle */}
              {techLocation.accuracy && (
                <Circle
                  center={techCoords}
                  radius={techLocation.accuracy}
                  pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.1 }}
                />
              )}
            </>
          )}

          {/* Fault Location Marker */}
          {faultCoords && (
            <Marker position={faultCoords} icon={faultLocationIcon}>
              <Popup>
                <div className="text-center">
                  <strong>Arıza Lokasyonu</strong>
                  <br />
                  <span className="text-xs text-gray-500">
                    {faultLocation.address || 'Adres bilgisi yok'}
                  </span>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-blue-500"></span>
          Usta konumu
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500"></span>
          Arıza lokasyonu
        </div>
      </div>
    </div>
  );
};

export default TechnicianLocationMap;
